import React, { useState } from 'react';
import {
  useKimTokenApprove,
  useConvertTokens
} from '@app/hooks/ryo/useConvert';
import { TransactionError } from '@thirdweb-dev/react';
import { Box, Button, Divider, HStack, Stack, Text } from '@chakra-ui/react';
import { NetworkMismatchButton } from '@app/components/NetworkMismatchButton';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { TransactionResultModal } from '@app/components/TransactionResultModal';
import { useLocationContext } from '@app/context/LocationContext';
import { LabeledValue } from '@app/components/LabeledValue';
import KimLogo from '@app/assets/images/logo/kimLogo.svg';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';
import { formatNumber } from '@app/helpers/format';

export const Convert = () => {
  const { isReadOnly } = useLocationContext();
  const [feedback, setFeedback] = useState<{
    status: 'success' | 'error';
    title: string;
    message: string;
    txHash?: string;
  } | null>(null);
  const {
    state,
    handleAmountChange,
    myRyoBalance,
    convert,
    converting,
    loadingTokenBalance,
    resetStateValues
  } = useConvertTokens();
  const isMismatchedNetwork = useNetworkMismatch();

  const {
    requiresApproval: requiresApproval,
    approve,
    isApproveInProgress: isApproveInProgress,
    getAllowance: getAllowance
  } = useKimTokenApprove(state.amount);

  const isBalanceExceeded =
    myRyoBalance !== undefined && +myRyoBalance < +state.amount;

  function renderInfoRow(label: string, value: string) {
    return (
      <HStack justifyContent="space-between">
        <Text fontSize="16px" fontWeight={400} lineHeight="26px" color="gray.4">
          {label}
        </Text>
        <Text fontSize="16px" fontWeight={500} lineHeight="26px" color="gray.4">
          {value}
        </Text>
      </HStack>
    );
  }

  function getSubmitLabel() {
    if (isBalanceExceeded) {
      return 'Insufficient balance';
    }

    if (!state.amount) {
      return 'Enter KIM amount to convert';
    }

    return 'Convert';
  }

  function renderSubmitAction() {
    if (isReadOnly) {
      return null;
    }

    if (isMismatchedNetwork) {
      return <NetworkMismatchButton />;
    }

    if (requiresApproval && !isBalanceExceeded) {
      return (
        <SubmitButton
          mt={4}
          loadingText="Processing..."
          onClick={async () => {
            try {
              const res = await approve();

              await getAllowance();

              const txHash = res?.receipt?.transactionHash;

              showSuccessToast(
                `Successfully approved KIM`,
                <ExternalLink txHash={txHash} />
              );
            } catch (e) {
              if (e instanceof TransactionError) {
                showErrorToast(e.reason);
              } else {
                showErrorToast(`Error approving KIM`);
              }
            }
          }}
          isLoading={isApproveInProgress}
          label="Approve"
        />
      );
    }

    return (
      <SubmitButton
        disabled={isBalanceExceeded || !+state.amount}
        mt={4}
        onClick={async () => {
          try {
            const res = await convert();

            if (res['reason'] !== undefined) {
              showErrorToast(res.reason);
            } else if (res instanceof Error) {
              showErrorToast(res.message);
            } else {
              resetStateValues();

              const txHash = res?.receipt?.transactionHash;

              showSuccessToast(
                `Successfully converted KIM to xKIM`,
                <ExternalLink txHash={txHash} />
              );

              setFeedback({
                status: 'success',
                title: 'Transaction Confirmed',
                message: 'Your transaction has been successfully completed',
                txHash
              });
            }
          } catch (e) {
            if (e instanceof TransactionError) {
              showErrorToast(e.reason);
            } else {
              showErrorToast(`Error converting KIM to xKIM`);
            }

            setFeedback({
              status: 'error',
              title: 'Transaction Error',
              message:
                'There was an error processing your transaction. Please check the details and try again'
            });
          }
        }}
        isLoading={converting}
        label={getSubmitLabel()}
      />
    );
  }

  return (
    <Box mt={4}>
      <HStack justifyContent="space-between">
        <Text fontSize="16px" fontWeight={400} color="neutral.300">
          Enter KIM amount
        </Text>
        <LabeledValue
          label="My KIM:"
          isLoading={loadingTokenBalance}
          value={myRyoBalance ? formatNumber(+myRyoBalance) : '-'}
          labelColor="gray.4"
        />
      </HStack>

      <HStack
        justifyContent="space-between"
        mt="8px"
        sx={{
          px: '14px',
          py: 2,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'gray.5',
          background: 'black'
        }}
      >
        <Stack>
          <HStack gap={2}>
            <Box
              border="1px solid #1B1B1B"
              p={2}
              rounded="full"
              justifyItems={'center'}
              alignItems={'center'}
            >
              <KimLogo fill="#FF4500" />
            </Box>
            <Text>KIM</Text>
          </HStack>
        </Stack>
        <HStack justify={'flex-end'}>
          <DebouncedInput
            amount={state.amount}
            onChange={handleAmountChange}
            color="#EBEBEB"
          />
          <Button
            variant="tertiary"
            onClick={() => {
              if (myRyoBalance === undefined) {
                return;
              }

              handleAmountChange(myRyoBalance);
            }}
            sx={{
              color: 'orange',
              bg: 'gray.2',
              width: 'fit-content',
              fontSize: '14px',
              padding: '8px 12px'
            }}
          >
            MAX
          </Button>
        </HStack>
      </HStack>
      <Text
        fontSize="16px"
        fontWeight={400}
        lineHeight="26px"
        color="gray.4"
        mt={4}
      >
        You will get
      </Text>

      <HStack justify="space-between" mt={4} px="14px">
        <HStack gap={2}>
          <Box
            bg="#FF4500"
            p={2}
            rounded="full"
            justifyItems={'center'}
            alignItems={'center'}
          >
            <KimLogo fill="#fff" />
          </Box>
          <Text>xKIM</Text>
        </HStack>
        <Text
          fontSize="26px"
          fontWeight={500}
          lineHeight="26px"
          color="neutral.100"
        >
          {state.amount || 0}
        </Text>
      </HStack>
      <Divider sx={{ margin: '28px 0', background: 'gray.5', height: '1px' }} />
      {renderInfoRow('Exchange Rate', '1 xKIM = 1 KIM')}
      {renderSubmitAction()}
      <TransactionResultModal
        isOpen={!!feedback}
        status={feedback?.status}
        title={feedback?.title}
        message={feedback?.message}
        txHash={feedback?.txHash}
        onClose={() => setFeedback(null)}
      />
    </Box>
  );
};
